import { A, useSearchParams, useNavigate, useParams, useBeforeLeave } from "@solidjs/router";
import {
  newStreamPlaybackRequest,
  newStreamRequest,
  useChatStreamingClient,
  newScope,
  ChatMessages,
  useChatClient,
  createConversationRequest,
  Chat,
  withBearerToken,
  useCallOptions,
  createListRecentChatsRequest,
} from "@storytell/cp-client";

import { createMemo, createSignal, Switch, Match, Show, onMount, Suspense } from "solid-js";
import { Logo } from "~/components/brand";
import { ErrorBoundary } from "~/components/error";
import {
  AppUIContextProvider,
  isExtendedPaneVisible,
  NavigationPaneMode,
  useAppUI,
} from "~/components/layouts/minimal/AppUIContext";
import { PromptBarWithStatus, type PromptSubmission } from "~/components/prompt";
import { useLanguageRegion } from "~/hooks/useLanguageRegion";
import { useI18n } from "~/i18n/context";
import { chatPageDictionary, type ChatPageMessages } from "./ChatStreamPage.i18n";
import { useWire } from "~/wire";
import { PromptBarAssetsCTA } from "~/components/prompt/PromptBarAssetsCTA";
import { HeroHeading } from "@storytell/ui/marketing";
import { useSync } from "~/lib/sync";
import { dispatchGlobalEvent } from "@storytell/events";
import { Avatar } from "~/components/avatar";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { TbLogout } from "solid-icons/tb";

import images from "./images.json";
import { twMerge } from "tailwind-merge";
import { ChatRenderer } from "~/components/chat/messages/ChatRenderer";

// Speed for playback, 1.0 is normal speed
const DEFAULT_PLAYBACK_SPEED = 5.0;

const ChatStreamPageContent = () => {
  const { state, actions } = useAppUI();
  const wire = useWire();
  const languageRegion = useLanguageRegion();
  const { currentLanguage, t } = useI18n();

  // Use the sync hook for asset synchronization
  const [syncState, syncActions] = useSync({
    enablePeriodicSync: true,
    syncIntervalMs: 30000, // 30 seconds for testing
    limit: 50,
    autoStartOnAuth: true,
  });

  // Type-safe translation helper for this page
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const translate = (key: keyof ChatPageMessages, params?: Record<string, any>) => {
    return t(key as string, chatPageDictionary[currentLanguage()] as unknown as Record<string, string>, params);
  };

  const isWaitingForAuth = () => !wire.auth.isReady();

  // Handle stopping streaming
  const handleStopStreaming = () => {
    // TODO: @andi streaming Cancel workflow
    // chat.setStatus(ChatStatus.DONE);
  };

  // Handle prompt submission
  const handlePromptSubmit = (submission: PromptSubmission) => {
    actions.submitPrompt();

    const workspaceId = wire.auth.realm().workspaceId;
    const projectId = wire.auth.realm().projectId;

    // Start the chat stream with the user's prompt, language region, and scope
    const scope = newScope(submission.scope);
    const locale = languageRegion();
    const submissionContent = submission.text;

    wire.chat.startOrContinueChat({
      submission: submissionContent,
      locale,
      projectId,
      workspaceId,
      scope,
    });
  };

  // TODO: @andi streaming Add playback

  const marginLeft = "margin-left";
  const marginRight = "margin-right";
  // Calculate layout margins to prevent overlap with panes
  const layoutStyles = createMemo(() => {
    const navWidth = state.navigationPane.mode === NavigationPaneMode.DOCKED ? state.navigationPane.width : 0;
    const extendedPaneWidth = isExtendedPaneVisible(state.extendedPane) ? state.extendedPane.width : 0;

    return {
      [marginLeft]: `${navWidth}px`,
      [marginRight]: `${extendedPaneWidth}px`,
    };
  });

  const initial = 22; //3;
  const [imageIndex, setImageIndex] = createSignal(initial);
  const nextImage = () => {
    if (imageIndex() < images.length - 1) {
      setImageIndex(imageIndex() + 1);
    } else {
      setImageIndex(0);
    }
  };
  const previousImage = () => {
    if (imageIndex() > 0) {
      setImageIndex(imageIndex() - 1);
    } else {
      setImageIndex(images.length - 1);
    }
  };

  onMount(() => {
    window.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight" && e.metaKey && e.ctrlKey) {
        nextImage();
      } else if (e.key === "ArrowLeft" && e.metaKey && e.ctrlKey) {
        previousImage();
      }
    });
  });

  const s = layoutStyles();

  return (
    <Suspense fallback={<LoadingPlayback />}>
      <div class="relative h-screen flex flex-col bg-canvas overflow-hidden">
        {/* Background Image - always present but fades based on state */}
        <div
          class={twMerge(
            "fixed z-0 inset-0 bg-cover bg-center bg-no-repeat pointer-events-none transition-opacity duration-1000 opacity-20 dark:opacity-30",
            wire.chat.shouldShowContent() && "opacity-0 dark:opacity-0",
          )}
          style={{
            "background-image": `url(/images/bg/${images[imageIndex()][2]})`,
          }}
        />
        {/* footer - only visible in initial state */}
        <div
          class="fixed z-20 bottom-0 left-0 right-0 text-sm bg-inset text-muted-text px-3 py-2 rounded-lg backdrop-blur-sm transition-opacity duration-1000"
          style={{
            opacity: wire.chat.isChatStreaming() || wire.chat.getChat() ? "0" : "1",
          }}
        >
          <div class="flex flex-col items-center text-center">
            <p class="mb-0">
              <span class="hidden sm:inline">
                Enjoy this image of{" "}
                <a href={images[imageIndex()][1]} class="underline hover:text-brand" target="_blank" rel="noreferrer">
                  {images[imageIndex()][0]}
                </a>
              </span>{" "}
              {translate("agreement_prefix")}{" "}
              <A href="/terms" class="underline hover:text-brand">
                {translate("terms")}
              </A>{" "}
              {translate("and")}{" "}
              <A href="/privacy" class="underline hover:text-brand">
                {translate("privacy_policy")}
              </A>
              .
            </p>
          </div>
        </div>

        <Switch>
          <Match when={isWaitingForAuth()}>
            <LoadingPlayback /> {/* Loading playback while waiting for auth */}
          </Match>
          {/* TODO: @andi streaming Add error display */}
          {/* <Match when={chat.chatState.status === ChatStatus.ERROR}>
          <StreamingErrorDisplay
            error={chat.chatState.error}
            onRetry={() => {
              // Reset the chat state and go back to initial state
              setHasSubmittedPrompt(false);
              setIsLayoutTransitioned(false);
              chat.setStatus(ChatStatus.READY);
            }}
          />
        </Match> */}
          <Match when={!wire.chat.shouldShowContent()}>
            <main data-testid="initial-welcome-state" class="z-10 flex-1 flex flex-col items-center">
              <nav class="w-full flex justify-between items-center pl-20 pr-10 py-5">
                <div class="flex gap-10 tracking-wider">
                  <a href="/" class="hover:underline underline-offset-2">
                    Platform
                  </a>
                  <a href="/pricing" class="hover:underline underline-offset-2">
                    Pricing
                  </a>
                  <a href="https://web.storytell.ai/book-a-demo" class="hover:underline underline-offset-2">
                    Book Enterprise Demo
                  </a>
                </div>

                <div class="flex gap-4 items-center">
                  <Show
                    when={!wire.auth.isRegistered()}
                    fallback={
                      <StDropdown
                        modal={false}
                        placement="bottom-end"
                        gutter={5}
                        contentProps={{
                          class: "z-10",
                        }}
                        content={
                          <>
                            <div class="text-sm font-medium text-secondary-text rounded-md px-2 py-2 pb-1">
                              {wire.auth.user()?.displayName || "You"}
                            </div>
                            <Show when={wire.auth.user()?.primaryEmail !== wire.auth.user()?.displayName}>
                              <div class="text-muted-text text-sm px-2 pb-2">{wire.auth.user()?.primaryEmail}</div>
                            </Show>
                            <StDropdownItem
                              onClick={() => wire.auth.methods.signOut()}
                              class="cursor-pointer flex items-center gap-3 hover:bg-canvas-hover rounded-md px-2 py-2"
                            >
                              <TbLogout class="size-5" />
                              Sign out
                            </StDropdownItem>
                          </>
                        }
                      >
                        <StDropdownTrigger class="cursor-pointer rounded-full">
                          <Avatar
                            class="ring-3"
                            src={wire.auth.user()?.avatarUrl}
                            name={wire.auth.user()?.displayName}
                            size="sm"
                          />
                        </StDropdownTrigger>
                      </StDropdown>
                    }
                  >
                    <A href="/auth/login" class="font-medium px-2 py-1">
                      Log in
                    </A>
                    <A href="/auth/signup" class="font-medium px-4 py-1 rounded text-on-brand bg-brand shadow-xl">
                      Sign up
                    </A>
                  </Show>
                </div>
              </nav>

              <div aria-hidden class="h-12" />
              <div class="text-center w-full">
                <div>
                  <Logo class="mx-auto" size={230} />
                </div>
                <div>
                  <HeroHeading />
                </div>
                <div class="h-8" />
                {/* Centered prompt bar in initial state */}
                <div class="w-full mx-auto space-y-4 px-4 sm:px-6 md:px-8 lg:px-0">
                  {/* TODO: @andi streaming Add streaming state */}
                  <PromptBarWithStatus
                    onSubmit={handlePromptSubmit}
                    // streamingState={streamingState}
                    onStopStreaming={handleStopStreaming}
                    //  chat={chat}
                  />

                  <div class="flex justify-center">
                    <PromptBarAssetsCTA
                      onOpenFilePicker={() => {
                        dispatchGlobalEvent("open-file-picker", undefined);
                      }}
                    />
                  </div>
                </div>
              </div>
            </main>
          </Match>
          <Match when={true}>
            <main
              class="flex-1"
              style={{
                height: "0",
                [marginLeft]: s[marginLeft],
                [marginRight]: s[marginRight],
              }}
            >
              <div class="h-full overflow-y-scroll">
                <div class="max-w-4xl mx-auto px-4 pt-18 pb-32">
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingPlayback />}>
                      <Show when={wire.chat.getChatId()} fallback={<LoadingPlayback />}>
                        {(chatId) => <ChatRenderer chatId={chatId()} />}
                      </Show>
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </div>

              {/* Fixed Blur backdrop to hide any messages between the prompt bar and the viewport bottom  */}
              <div
                aria-hidden
                class="absolute bottom-0 left-0 right-0 h-25 pointer-events-none backdrop-blur-xs bg-canvas/20"
              />
              {/* Fixed Prompt Bar - positioned within same container as StreamRenderer */}
              <div class="absolute bottom-6 left-0 right-0 pointer-events-none px-4">
                <div class="w-full mx-auto space-y-4 px-4 sm:px-6 md:px-8 lg:px-0 pointer-events-auto">
                  <div class="max-w-4xl mx-auto">
                    {/* TODO: @andi streaming Add streaming state */}
                    <PromptBarWithStatus onSubmit={handlePromptSubmit} onStopStreaming={handleStopStreaming} />
                  </div>
                </div>
              </div>
            </main>
          </Match>
        </Switch>
      </div>
    </Suspense>
  );
};

export const ChatStreamPage = () => {
  return (
    <AppUIContextProvider>
      <ChatStreamPageContent />
    </AppUIContextProvider>
  );
};

const LoadingPlayback = () => {
  return (
    <div data-testid="loading-playback-state" class="flex-1 flex flex-col items-center justify-center relative z-10">
      <div class="max-w-md mx-auto px-6">
        <div class="rounded-3xl border border-primary-border backdrop-blur-xl bg-tile p-12 text-center">
          <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-brand mx-auto mb-6" />
          <p class="text-xl text-primary-text font-medium mb-2">Loading...</p>
          <p class="text-sm text-secondary-text">Preparing your conversation</p>
        </div>
      </div>
    </div>
  );
};
